// Stock counts, read only (kitchen flow pilot, Batch 6): the manager's view of
// count sessions, under Reports → Kitchen flow → Counts. The manager has no
// Store Inventory, so this is where they see what the store and kitchen
// counted. Lists every place's counts; opening one shows its counted lines.
// No approve, cancel or edit here — and no money (counts carry none).
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { CT } from '@/lib/inventoryFlow/countStrings'
import { readOnlyCountLines } from '@/lib/inventoryFlow/countLogic'
import type { CountSession } from '@/lib/inventoryFlow/visibilityTypes'
import { cn, formatDateTimeHuman } from '@/lib/utils'
import { LoadError } from './LoadError'

const fmt = (n: number) => String(Math.round(n * 1000) / 1000)

export function CountsReadOnly() {
  const [openId, setOpenId] = useState<string | null>(null)
  if (openId) return <CountReadOnly id={openId} onBack={() => setOpenId(null)} />
  return <CountList onOpen={setOpenId} />
}

function CountList({ onOpen }: { onOpen: (id: string) => void }) {
  const q = useQuery({ queryKey: ['inventory-flow', 'count-sessions', 'read-only'], queryFn: () => apiClient.getCountSessions({ limit: 50 }), staleTime: 30_000 })
  const sessions = q.data?.data ?? []
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold">{CT.read.title}</h2>
        <p className="text-sm text-muted-foreground">{CT.read.subtitle}</p>
      </div>
      {q.isLoading && <p className="text-sm text-muted-foreground">…</p>}
      {q.isError && !q.data && <LoadError busy={q.isFetching} onRetry={() => void q.refetch()} />}
      {q.data && sessions.length === 0 && <p className="text-sm text-muted-foreground">{CT.read.none}</p>}
      {sessions.length > 0 && (
        <ul className="divide-y rounded-lg border bg-card">
          {sessions.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center justify-between gap-3 p-3 text-sm">
              <div className="min-w-0 space-y-0.5">
                <p className="font-medium">{s.session_no} · {s.location.name}</p>
                <p className="text-muted-foreground">
                  {CT.status[s.status] ?? s.status}
                  {s.approved_by_name ? ` ${CT.by} ${s.approved_by_name}` : ''}
                  {' · '}{CT.counted(s.lines_counted, s.lines_total)}
                  {s.lines_with_variance != null ? ` · ${CT.differences(s.lines_with_variance)}` : ''}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => onOpen(s.id)}>{CT.read.open}</Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function CountReadOnly({ id, onBack }: { id: string; onBack: () => void }) {
  const [onlyDiff, setOnlyDiff] = useState(false)
  const q = useQuery({ queryKey: ['inventory-flow', 'count-session', id, 'read-only'], queryFn: () => apiClient.getCountSession(id) })
  const s: CountSession | undefined = q.data?.data
  const rows = s ? readOnlyCountLines(s.lines ?? [], onlyDiff) : []
  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" className="px-2" onClick={onBack}><ArrowLeft className="mr-2 h-4 w-4" /> {CT.read.back}</Button>
      {q.isLoading && <p className="text-sm text-muted-foreground">…</p>}
      {q.isError && !q.data && <LoadError busy={q.isFetching} onRetry={() => void q.refetch()} />}
      {s && (
        <>
          <div>
            <h2 className="text-base font-semibold">{s.session_no} · {s.location.name}</h2>
            <p className="text-sm text-muted-foreground">
              {CT.status[s.status] ?? s.status}
              {s.approved_by_name ? ` ${CT.by} ${s.approved_by_name}` : ''}
              {s.approved_at ? ` · ${formatDateTimeHuman(s.approved_at)}` : ''}
              {' · '}{CT.read.started(formatDateTimeHuman(s.created_at), s.created_by_name)}
            </p>
            <p className="text-sm text-muted-foreground">{CT.counted(s.lines_counted, s.lines_total)}</p>
          </div>
          {s.book_hidden ? (
            <p className="text-sm text-muted-foreground">{CT.read.bookHidden}</p>
          ) : (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="h-4 w-4" checked={onlyDiff} onChange={(e) => setOnlyDiff(e.target.checked)} />
              {CT.read.onlyDifferences}
            </label>
          )}
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">{onlyDiff ? CT.read.noDifferences : CT.read.noLines}</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    {CT.read.cols.map((c, i) => (s.book_hidden && i > 1 ? null : <th key={c} className={cn('px-3 py-2 font-semibold', i > 0 && 'text-right')}>{c}</th>))}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((l) => (
                    <tr key={l.stock_item_id}>
                      <td className="px-3 py-2">{l.item_name}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt(l.counted_qty ?? 0)} {l.unit}</td>
                      {!s.book_hidden && (
                        <>
                          <td className="px-3 py-2 text-right tabular-nums">{l.expected_qty != null ? `${fmt(l.expected_qty)} ${l.unit}` : '—'}</td>
                          <td className={cn('px-3 py-2 text-right tabular-nums', l.variance != null && l.variance < 0 && 'text-danger-fg', l.variance != null && l.variance > 0 && 'text-warning-fg')}>
                            {l.variance == null ? '—' : l.variance === 0 ? CT.matches : l.variance < 0 ? CT.less(fmt(-l.variance), l.unit) : CT.more(fmt(l.variance), l.unit)}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}
